import React, { useState } from "react";
import SidebarOption from "./SidebarOption";
import { Plus, Inbox, Users, Settings, HelpCircle } from "lucide-react";
import { useDispatch } from "react-redux";
import { NEW_REQUEST_PAGE, ACCOUNT, REQUEST } from "utils/reducers";
import HelpDialog from "./HelpDialog";

interface SidebarProps {
  inboxCount: number;
  isTeamInbox: boolean;
  setIsTeamInbox: (isTeamInbox: boolean) => void;
  isSearching: boolean;
  setIsSearching: (isSearching: boolean) => void;
}

// Simple button component for utility options
function SidebarUtilityButton({
  Icon,
  name,
  onClick,
}: {
  Icon: React.ComponentType<any>;
  name: string;
  onClick: () => void;
}) {
  return (
    <button
      className="flex items-center gap-3 px-4 py-2.5 mx-1 my-0.5 rounded-lg cursor-pointer transition-all duration-200 text-gray-700 hover:bg-gray-100 hover:text-gray-900 font-normal w-full text-left"
      onClick={onClick}
    >
      <Icon className="w-5 h-5" />
      <span className="text-sm">{name}</span>
    </button>
  );
}

function Sidebar({
  inboxCount,
  isTeamInbox,
  setIsTeamInbox,
  isSearching,
  setIsSearching,
}: SidebarProps) {
  const dispatch = useDispatch();
  const [showHelpDialog, setShowHelpDialog] = useState(false);

  return (
    <div className="flex flex-col h-full ">
      {/* Request Button */}
      <div className="p-6 pb-4">
        <button
          className="w-full flex items-center justify-center gap-2.5 px-6 py-3 bg-brand hover:bg-brand-hover text-white rounded-full font-medium text-sm transition-all duration-200 shadow-sm hover:shadow-md"
          onClick={() => {
            dispatch({ type: ACCOUNT.hide });
            dispatch({ type: NEW_REQUEST_PAGE.display });
          }}
        >
          <Plus className="w-4 h-4" />
          <span>New Request</span>
        </button>
      </div>

      {/* Navigation Options */}
      <nav className="flex-1 px-4 pt-2">
        <SidebarOption
          Icon={Inbox}
          name="My Inbox"
          number={inboxCount}
          active={!isSearching && !isTeamInbox}
          onClick={() => {
            dispatch({ type: ACCOUNT.hide });
            dispatch({ type: REQUEST.hide });
            dispatch({ type: NEW_REQUEST_PAGE.hide });
            setIsSearching(false);
            setIsTeamInbox(false);
          }}
        />
        <SidebarOption
          Icon={Users}
          name="Team Inbox"
          number={inboxCount}
          active={!isSearching && isTeamInbox}
          onClick={() => {
            dispatch({ type: ACCOUNT.hide });
            dispatch({ type: REQUEST.hide });
            dispatch({ type: NEW_REQUEST_PAGE.hide });
            setIsSearching(false);
            setIsTeamInbox(true);
          }}
        />
      </nav>

      {/* Utility Options - Mobile Only */}
      <div className="md:hidden border-t border-gray-200 px-4 py-3">
        <SidebarUtilityButton
          Icon={Settings}
          name="Account"
          onClick={() => dispatch({ type: ACCOUNT.display })}
        />
        <SidebarUtilityButton
          Icon={HelpCircle}
          name="Help"
          onClick={() => setShowHelpDialog(true)}
        />
      </div>

      {/* Help Dialog */}
      <HelpDialog open={showHelpDialog} onOpenChange={setShowHelpDialog} />
    </div>
  );
}

export default Sidebar;
